/**
 * What the home screen says about a game before anyone has played it: its
 * full name, one sentence on how it is played, and the cards a player of that
 * game would recognise as its own. The fan is the screen's one memorable
 * thing, so each game gets the hand that is characteristic of it rather than
 * the same five cards with a different heading.
 */
import type { CardId, GameId } from '@cards/shared'

export interface GameIntro {
  /** The name a player says out loud; the tab may shorten it. */
  readonly name: string
  /** One sentence, plain words, no rules lawyering. */
  readonly line: string
  /** Left to right as fanned. */
  readonly fan: readonly CardId[]
}

const INTROS: Record<string, GameIntro> = {
  // The Joker and both bowers, sorted as a hand: the three cards that make
  // 500 what it is, with the Joker on top where it can be seen whole.
  'five-hundred': {
    name: '500',
    line: 'Four players in pairs. Bid for tricks with the Joker and both bowers on your side, and race to 500.',
    fan: ['13H', '14H', '11D', '11H', 'JOKER'],
  },
  // Trumps in Belote's own order, King up to Jack: the Nine and Jack over
  // the Ace is the first thing Belote teaches.
  belote: {
    name: 'Belote',
    line: 'Four players in pairs with 32 cards. The Jack and Nine of trumps outrank the Ace.',
    fan: ['13S', '10S', '14S', '9S', '11S'],
  },
  // Four Jacks: the highest declaration in the game, worth 200.
  belot: {
    name: 'Bulgarian Belot',
    line: 'Pairs or head to head. One auction from Clubs to All Trumps, declarations, and first to 151.',
    fan: ['11C', '11D', '11H', '11S'],
  },
  // The scoring cards of Bonanza-style 9-5-2: high, nine, five, jack, low.
  pitch: {
    name: 'Pitch',
    line: 'Four players in pairs. Win the High, Low, Jack, the Nine and the Five of trumps, and race to 52.',
    fan: ['2C', '5C', '9C', '11C', '14C'],
  },
  // A top spade sequence: honours.
  bridge: {
    name: 'Bridge',
    line: 'Four players in pairs. A full auction, dummy face up, and duplicate scoring over four boards.',
    fan: ['10S', '11S', '12S', '13S', '14S'],
  },
}

export function introOf(id: GameId): GameIntro {
  return INTROS[id] ?? INTROS['five-hundred']!
}
